import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import SellerAuth from ".";
import {
  GET_ALL_PURSES,
  GET_CURRENT_SUBSCRIPTION_PLAN,
  GET_SELLERS_PLANS,
} from "../../Utils/Queries";
import { UPGRADE_SUBSCRIPTION_PLAN } from "../../Utils/Mutations";
import AddCash from "../../Account-Profile/Components/Mamapurse/AddCash";
import { buildSelectablePlans } from "./select-plan-components/plan-config";
import { PlanDetailScreen } from "./select-plan-components/plan-detail-screen";
import { PlanListScreen } from "./select-plan-components/plan-list-screen";

const SelectSellerPlan = () => {
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [screen, setScreen] = useState("plans");
  const navigate = useNavigate();
  const location = useLocation();
  const addCashRef = useRef(null);
  const hasSyncedCurrentPlanRef = useRef(false);
  const hasUserSelectedPlanRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const profile = useSelector(
    (state) => state.user?.user?.profile || state.user?.profile,
  );
  const hasStore = Boolean(profile?.store || profile?.hasPaidMembershipFee);
  const isUpgradeFlow =
    isAuthenticated && (hasStore || location.state?.from === "dashboard");

  const { data: plansData } = useQuery(GET_SELLERS_PLANS, {
    fetchPolicy: "cache-and-network",
  });
  const { data: currentSubscriptionData } = useQuery(
    GET_CURRENT_SUBSCRIPTION_PLAN,
    {
      fetchPolicy: "cache-and-network",
      skip: !isUpgradeFlow,
    },
  );
  const {
    data: purseData,
    loading: walletLoading,
    refetch: refetchWallet,
  } = useQuery(GET_ALL_PURSES, {
    fetchPolicy: "cache-and-network",
    skip: !isUpgradeFlow,
  });
  const [upgradeSubscriptionPlan, { loading: upgradeLoading }] = useMutation(
    UPGRADE_SUBSCRIPTION_PLAN,
  );

  const plans = useMemo(
    () => buildSelectablePlans(plansData?.fetchAllPlans),
    [plansData],
  );
  const currentPlanCode = String(
    currentSubscriptionData?.getCurrentSubscriptionPlan?.plan?.code ?? "",
  ).toLowerCase();

  useEffect(() => {
    if (
      !isUpgradeFlow ||
      hasSyncedCurrentPlanRef.current ||
      hasUserSelectedPlanRef.current ||
      !currentPlanCode
    ) {
      return;
    }

    if (plans.some((plan) => plan.id === currentPlanCode)) {
      setSelectedPlan(currentPlanCode);
      hasSyncedCurrentPlanRef.current = true;
    }
  }, [currentPlanCode, isUpgradeFlow, plans]);

  useEffect(() => {
    if (!plans.some((plan) => plan.id === selectedPlan)) {
      setSelectedPlan(plans[0]?.id ?? "free");
    }
  }, [plans, selectedPlan]);

  const selected = useMemo(
    () => plans.find((plan) => plan.id === selectedPlan) ?? plans[0],
    [plans, selectedPlan],
  );

  const walletBalance = useMemo(() => {
    const wallets = purseData?.getWallets ?? [];
    const wallet = wallets.find((item) => item?.isDefault) ?? wallets[0];
    return Number(wallet?.spendingBalance) || 0;
  }, [purseData]);

  const selectedPlanAmount =
    Number(
      selected?.raw?.price ??
        String(selected?.price ?? "").replace(/[^0-9.]/g, ""),
    ) || 0;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(isAuthenticated ? "/seller/dashboard" : "/sell-on-mamaket");
  };

  const saveSelectedPlan = () => {
    localStorage.setItem("sellerSelectedPlan", selected.id);
    if (selected.planId) {
      localStorage.setItem("sellerSelectedPlanId", selected.planId);
    }
  };

  const finishSelection = async () => {
    saveSelectedPlan();

    if (!isAuthenticated) {
      navigate("/seller-signup", {
        state: { selectedPlan: selected.id, selectedPlanId: selected.planId },
      });
      return;
    }

    if (hasStore || location.state?.from === "dashboard") {
      if (selected.id === "free") {
        if (!selected?.planId) {
          enqueueSnackbar("Plan is still loading. Please try again.", {
            variant: "warning",
          });
          return;
        }

        try {
          await upgradeSubscriptionPlan({
            variables: { planId: selected.planId },
            refetchQueries: [{ query: GET_CURRENT_SUBSCRIPTION_PLAN }],
            awaitRefetchQueries: true,
          });
          enqueueSnackbar("Plan updated successfully.", {
            variant: "success",
          });
        } catch (error) {
          enqueueSnackbar(
            error?.graphQLErrors?.[0]?.message ||
              error?.message ||
              "Could not update subscription plan.",
            { variant: "error" },
          );
          return;
        }
      }

      navigate("/seller/dashboard", {
        state: { selectedPlan: selected.id, selectedPlanId: selected.planId },
      });
      return;
    }

    navigate("/seller/create-store", {
      state: { selectedPlan: selected.id, selectedPlanId: selected.planId },
    });
  };

  const handlePlanSelect = (planId) => {
    hasUserSelectedPlanRef.current = true;
    setSelectedPlan(planId);
  };

  const handleContinueFromPlans = () => {
    setScreen("details");
  };

  const handleUpgradeStandard = async () => {
    if (!selected?.planId) {
      enqueueSnackbar("Plan is still loading. Please try again.", {
        variant: "warning",
      });
      return;
    }

    if (walletBalance < selectedPlanAmount) {
      enqueueSnackbar("Insufficient wallet balance. Please top up first.", {
        variant: "warning",
      });
      return;
    }

    try {
      await upgradeSubscriptionPlan({
        variables: { planId: selected.planId },
        refetchQueries: [{ query: GET_CURRENT_SUBSCRIPTION_PLAN }],
        awaitRefetchQueries: true,
      });
      enqueueSnackbar("Subscription upgraded successfully.", {
        variant: "success",
      });
      saveSelectedPlan();
      await refetchWallet?.();
      navigate("/seller/dashboard", {
        state: { selectedPlan: selected.id, selectedPlanId: selected.planId },
      });
    } catch (error) {
      enqueueSnackbar(
        error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          "Could not upgrade subscription plan.",
        { variant: "error" },
      );
    }
  };

  const handleTopUpWallet = () => {
    addCashRef.current?.click();
  };

  return (
    <SellerAuth>
      <div className="mx-auto flex min-h-[calc(100dvh-80px)] max-w-[70%] flex-col justify-center px-5 py-8 sm:px-8 lg:min-h-full">
        <AnimatePresence mode="wait">
          {screen === "plans" ? (
            <PlanListScreen
              plans={plans}
              selected={selected}
              selectedPlan={selectedPlan}
              onBack={handleBack}
              onContinue={handleContinueFromPlans}
              onPlanSelect={handlePlanSelect}
            />
          ) : (
            <PlanDetailScreen
              selected={selected}
              onBackToPlans={() => setScreen("plans")}
              onContinue={finishSelection}
              walletBalance={walletBalance}
              walletLoading={walletLoading}
              isWalletUpgradeFlow={isUpgradeFlow}
              upgradeLoading={upgradeLoading}
              onUpgradeStandard={handleUpgradeStandard}
              onTopUpWallet={handleTopUpWallet}
              onViewStandard={() => {
                hasUserSelectedPlanRef.current = true;
                setSelectedPlan("standard");
              }}
            />
          )}
        </AnimatePresence>
        {isUpgradeFlow ? (
          <div className="hidden">
            <AddCash
              ref={addCashRef}
              text="Top Up Wallet"
              onPaymentSuccess={async () => {
                enqueueSnackbar("Wallet funded successfully.", {
                  variant: "success",
                });
                await refetchWallet?.();
              }}
              onPaymentError={(error) => {
                enqueueSnackbar(error?.message ?? "Wallet funding failed.", {
                  variant: "error",
                });
              }}
            />
          </div>
        ) : null}
      </div>
    </SellerAuth>
  );
};

export default SelectSellerPlan;